import * as React from "react"
import { useRouter } from "next/router"

import { Center, Spinner, useToast } from "@chakra-ui/react"

import { intersects } from "@supliful/domain"

import { REDIRECT_PATH } from "lib/config"
import type { Admin } from "lib/graphql"
import { AdminPermission } from "lib/graphql"
import { useMe } from "lib/hooks/useMe"

export type WithAdminAuthProps = {
  admin: Admin
}

export const withAuth = <TOriginalProps extends WithAdminAuthProps>(
  Page: React.ComponentType<TOriginalProps>,
  permissions: AdminPermission[] = [],
) => {
  return (props: Omit<TOriginalProps, keyof WithAdminAuthProps>) => {
    const { me, loading } = useMe()
    const router = useRouter()
    const toast = useToast()

    const isAuthenticated = React.useMemo(() => {
      return !!me
    }, [me])

    const isAuthorized = React.useMemo(() => {
      if (!me) return false
      if (permissions.length === 0) return true
      return intersects(
        [AdminPermission.Everything, ...permissions],
        me.permissions,
      )
    }, [me])

    React.useEffect(() => {
      if (loading) return
      if (!isAuthenticated) {
        if (router.asPath === "/") {
          router.replace(`/login`)
        } else {
          router.replace(`/login?${REDIRECT_PATH}=${router.asPath}`)
        }
      } else if (!isAuthorized) {
        toast({ status: "error", description: "Not Authorized" })
      }
    }, [loading, isAuthenticated, isAuthorized, router, toast])

    if (loading) {
      return (
        <Center minH="100vh">
          <Spinner />
        </Center>
      )
    }
    const newProps = {
      ...props,
      admin: me,
    } as TOriginalProps
    return isAuthorized ? <Page {...newProps} /> : <></>
  }
}
